/**
 * Le mobilier des enclos: abreuvoir, ratelier de foin, abri.
 *
 * POURQUOI CE N'EST PAS UN BATIMENT. L'enclos n'est pas un modele qu'on pose,
 * c'est une aire que le joueur DESSINE a la cloture (voir shared/sim/zoo.ts).
 * Son mobilier apparait donc tout seul, au centre de l'aire, le jour ou un
 * animal vient y vivre: c'est la reponse visible du jeu a « j'ai bien fait le
 * tour ». Rien a payer, rien a placer, rien a rater.
 *
 * BUDGET. Tous les enclos du monde tiennent dans UNE geometrie fusionnee, donc
 * un seul appel de dessin (deux avec l'ombre), et elle n'est refabriquee que
 * quand la liste des foyers change — pas a l'image. Aucune lumiere n'est
 * ajoutee: le nombre de lumieres fait partie de la clef du cache de shaders de
 * three.js, en ajouter une ferait tout recompiler.
 */
import * as THREE from 'three'
import { fusionner, tr, type Partie } from './geometrie.ts'

/** Un foyer a meubler: le centre d'un enclos et son rayon utile. */
export interface FoyerRendu {
  x: number
  z: number
  /** Altitude du sol au centre. */
  y: number
  rayon: number
}

const BOIS = 0x9a6b3f
const BOIS_CLAIR = 0xc09159
const PIERRE = 0x9aa4ac
const EAU = 0x4fa8d8
const FOIN = 0xd9bf6a

function boite(
  parties: Partie[],
  couleur: number,
  taille: [number, number, number],
  pos: [number, number, number],
  rot?: [number, number, number],
): void {
  parties.push({
    geo: new THREE.BoxGeometry(taille[0], taille[1], taille[2]),
    couleur,
    transforme: tr(pos, rot),
  })
}

/**
 * Meuble un enclos. Le mobilier se range sur un cercle a mi-rayon: assez au
 * bord pour laisser la place aux betes, assez loin de la cloture pour ne jamais
 * la traverser, quelle que soit la forme dessinee.
 */
function meublerEnclos(parties: Partie[], f: FoyerRendu): void {
  const r = Math.max(1, Math.min(f.rayon * 0.55, 3.2))
  const y = f.y

  // Abreuvoir: un bac de pierre plein d'eau.
  const ax = f.x + r
  boite(parties, PIERRE, [1.5, 0.42, 0.85], [ax, y + 0.21, f.z])
  boite(parties, EAU, [1.3, 0.06, 0.65], [ax, y + 0.4, f.z])

  // Ratelier: deux montants, une traverse, et le foin qui deborde.
  const fx = f.x - r
  for (const s of [-1, 1]) {
    boite(parties, BOIS, [0.1, 0.9, 0.1], [fx + s * 0.5, y + 0.45, f.z])
  }
  boite(parties, BOIS_CLAIR, [1.2, 0.09, 0.5], [fx, y + 0.86, f.z], [0.25, 0, 0])
  parties.push({
    geo: new THREE.ConeGeometry(0.42, 0.5, 7),
    couleur: FOIN,
    transforme: tr([fx, y + 0.62, f.z]),
  })

  // Abri: quatre poteaux et un toit incline, ouvert sur trois cotes.
  const sz = f.z + r
  for (const sx of [-1, 1]) {
    for (const dz of [-0.7, 0.7]) {
      boite(parties, BOIS, [0.13, 1.5, 0.13], [f.x + sx * 0.85, y + 0.75, sz + dz])
    }
  }
  boite(parties, BOIS_CLAIR, [2.2, 0.12, 1.9], [f.x, y + 1.6, sz], [0.16, 0, 0])
  // Le fond, pour que l'abri abrite vraiment de quelque chose.
  boite(parties, BOIS, [2.1, 1, 0.1], [f.x, y + 1, sz + 0.75])
}

/** Signature d'une liste de foyers: ce qui decide de refabriquer la geometrie. */
function signature(foyers: readonly FoyerRendu[]): string {
  let s = ''
  for (const f of foyers) s += `${f.x.toFixed(1)},${f.z.toFixed(1)},${f.rayon.toFixed(1)};`
  return s
}

export class RenduZoo {
  readonly groupe: THREE.Group
  private scene: THREE.Scene
  private materiau: THREE.MeshLambertMaterial
  private maillage: THREE.Mesh | null = null
  private signatureCourante = ''
  private nombre = 0

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.groupe = new THREE.Group()
    this.groupe.name = 'zoo'
    scene.add(this.groupe)
    this.materiau = new THREE.MeshLambertMaterial({ vertexColors: true })
  }

  /**
   * Met le mobilier en accord avec la liste des enclos habites. Sans effet si
   * rien n'a bouge: c'est ce qui permet de l'appeler a chaque image.
   */
  maj(foyers: readonly FoyerRendu[]): void {
    const sig = signature(foyers)
    if (sig === this.signatureCourante) return
    this.signatureCourante = sig
    this.vider()
    this.nombre = foyers.length
    if (foyers.length === 0) return

    const parties: Partie[] = []
    for (const f of foyers) meublerEnclos(parties, f)
    const geo = fusionner(parties)
    const m = new THREE.Mesh(geo, this.materiau)
    m.name = 'zoo:mobilier'
    m.castShadow = true
    m.receiveShadow = true
    this.maillage = m
    this.groupe.add(m)
  }

  private vider(): void {
    if (!this.maillage) return
    this.groupe.remove(this.maillage)
    this.maillage.geometry.dispose()
    this.maillage = null
  }

  /** Nombre d'enclos meubles. Pour le journal et les tests. */
  nombreEnclos(): number {
    return this.nombre
  }

  dispose(): void {
    this.vider()
    this.materiau.dispose()
    this.scene.remove(this.groupe)
  }
}
